import { useState, useContext, useRef } from "react";

import Box from "@mui/material/Box";
// import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
// import Typography from "@mui/material/Typography";

import { EventForm, SimpleActionDialog } from "../../../components";
import { Loading } from "react-admin";
import { RiHomeHeartFill } from "react-icons/ri";
import { theme } from "../../../theme";
import { routePaths } from "../../../baseLayout/SidebarMenu";
import { useRedirect } from "react-admin";
import { StoreContext } from "../../../store/Store";
import MediaImageUploader from "../../../components/MediaImageUploader/MediaImageUploader";
import { csvData } from "../CreateEventFlow";
import NotificationMessage, {
  notificationType,
} from "../../../components/NotificationMessage/NotificationMessage";
import moment from "moment";
import { jobStatus } from "../../../dataProvider/interfaces/IEvent";
import { APIContext } from "../../../store/APIStore";
import { MdWarning } from "react-icons/md";
import FdTypography from "../../../components/UIComponents/Typography/FdTypography";
import FdButton from "../../../components/UIComponents/Button/FdButton";
import {newEventDetails as classes} from "../styles";

export type IProps = {
  event?: any;
  issueButtonClick: Function;
  attachedAttendees?: csvData;
  csvFieldsMatch: any;
};



const NewEventDetails = ({
  issueButtonClick,
  attachedAttendees,
  csvFieldsMatch,
}: IProps) => {
  const redirect = useRedirect();
  const [loading, setLoading] = useState(false);
  const [eventImage, setEventImage] = useState<File>();
  const [actionDialog, setActionDialog] = useState<any>({
    open: false,
  });
  const [dirtyForm, setDirtyForm] = useState(false);
  const [, dispatch] = useContext(StoreContext);
  const api = useContext(APIContext);
  const notificationRef = useRef<any>();
  let jobChecker: any;

  const handleCreateEvent = () => {
    const form = document.getElementById("eventForm");
    if (form) {
      form.dispatchEvent(
        new Event("submit", { cancelable: true, bubbles: true })
      );
    }
  };

  const handleIssueButton = () => {
    if (dirtyForm) {
      showLeaveWarningDialog();
    } else {
      issueButtonClick();
    }
  };

  const showLeaveWarningDialog = () => {
    setActionDialog({
      open: true,
      icon: <MdWarning size="45" color={theme.palette.primary.main} />,
      title: "Unsaved changes",
      content:
        "Are you sure you want to leave this step? All your changes will be lost.",
      onCancel: () => {
        setActionDialog({ open: false });
      },
      onConfirm: () => {
        issueButtonClick();
      },
    });
  };

  const onFormSubmit = async (values: any) => {
    setLoading(true);

    // create new event
    const newEvent = await createNewEvent(values);

    if (newEvent) {
      dispatch({ type: "SET_SELECTED_EVENT", payload: newEvent });
    }

    // attach event image
    if (newEvent && eventImage) {
      const imageData = await uploadEventImage(newEvent, eventImage);
      const updatedEvent = await updateEvent({
        ...newEvent,
        archivingPeriod: `${newEvent.archivingPeriod}`,
        logoUrl: imageData.image,
      });
      dispatch({ type: "SET_SELECTED_EVENT", payload: updatedEvent });
    }

    // attach event attendees
    if (newEvent) {
      if (attachedAttendees && attachedAttendees.file) {
        const csvData = await uploadAttachedCSV(newEvent.id); // upload csv file
        if (csvData) {
          const csvJobData = await processAttachedCSV(
            newEvent.id,
            csvData.filePath
          ); // start a file processing job

          jobChecker = setInterval(() => {
            checkJobStatus(newEvent.id, csvJobData.job);
          }, 3000); // check for jobs status every 3s
        }
      } else {
        showSuccessDialog(newEvent.id);
      }
    }
  };

  const showSuccessDialog = (newEventId: number) => {
    setLoading(false);
    setActionDialog({
      open: true,
      title: "Event created!",
      content:
        "Congrats! Let us take you to your event form, where you will gain full control of your event!",
      confirmLabel: "View event",
      icon: <RiHomeHeartFill size="45" color={theme.palette.primary.main} />,
      onConfirm: () => {
        onDialogActionClick(newEventId);
      },
    });
  };

  const createNewEvent = async (params: any) => {
    try {
      const event = (await api.eventService.createEvent({
        name: params.eventName,
        archivingPeriod: params.eventArchiveInterval,
        endDateTime: moment(params.eventEndDate).format("MMMM DD YYYY HH:mm"),
        group: params.eventGroup,
        startDateTime: moment(params.eventStartDate).format(
          "MMMM DD YYYY HH:mm"
      ),
        timeZone: params.eventTimezone,
        city: params.eventCity,
        locationName: params.eventLocation,
        hasFraudDetection: Boolean(params?.hasFraudDetection),
      })) as any;
      return event;
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const checkJobStatus = async (eventId: number, jobId: number) => {
    try {
      const { status, result } = (await api.eventService.getEventJob(
        eventId,
        jobId
      )) as any;
      if (status === jobStatus.FINISHED) {
        clearInterval(jobChecker);
        showSuccessDialog(eventId);
      }
      if (status === jobStatus.ERROR) {
        clearInterval(jobChecker);
        notificationRef.current?.show(result, notificationType.Error);
      }
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const uploadAttachedCSV = async (eventId: number) => {
    try {
      if (attachedAttendees && attachedAttendees.file) {
        const csvData = (await api.fileService.uploadCSVFile(
          eventId,
          attachedAttendees.file.name,
          attachedAttendees.file
        )) as any;
        return csvData;
      }
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const processAttachedCSV = async (eventId: number, filePath: string) => {
    try {
      const fileData = (await api.attendeeService.processEventAttendeesCSV(
        eventId,
        filePath,
        csvFieldsMatch
      )) as any;
      return fileData;
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  // const getJobStatus = async (eventId: number, jobId: number) => {
  //   try {
  //     const { status, result } = (await api.eventService.getEventJob(
  //       eventId,
  //       jobId
  //     )) as any;
  //     return { status, result };
  //   } catch (error: any) {
  //     console.log(error.response);
  //     notificationRef.current?.show(
  //       error.response.data.message,
  //       notificationType.Error
  //     );
  //   }
  // };

  // const loadEvent = async (id: number) => {
  //   try {
  //     const event = await api.eventService.getEvent({ id });
  //     return event;
  //   } catch (error: any) {
  //     console.log(error.response);
  //     notificationRef.current?.show(
  //       error.response.data.message,
  //       notificationType.Error
  //     );
  //   }
  // };

  const uploadEventImage = async (event: any, file: File) => {
    try {
      const image = (await api.fileService.uploadImageFile("events", {
        resourceId: event.id,
        file: file,
      })) as any;
      return image;
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const updateEvent = async (event: any) => {
    try {
      const newEvent = await api.eventService.updateEvent(event);
      return newEvent;
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const handleImageSelection = (file: any) => {
    setEventImage(file);
  };

  const onDialogActionClick = (newEventId: number) => {
    redirect(`/${routePaths.EVENTS}/${newEventId}/${routePaths.EDIT}`);
  };

  if (loading) {
    return (
      <>
        <Loading />
        <NotificationMessage ref={notificationRef} />
      </>
    );
  }

  return <>
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      textAlign="center"
      maxWidth="30%"
    >
      <Box sx={classes.formWrapper}>
        <Grid container sx={classes.wrapper} justifyContent="center">
          <Grid key={"123"} item xs={2}>
            <MediaImageUploader onImageSelect={handleImageSelection} />
          </Grid>
        </Grid>
        <FdTypography
          label="New event details"
          variant="h2"
          component="h2"
          sx={{ marginBottom: "20px" }} />
          
        {/* <Typography style={{ marginBottom: "15px" }}>
          Here is the list of your events attendees. Click on one of them if
          you would like to check if their info is imported in correctly.
        </Typography> */}

        <EventForm
          formSubmit={onFormSubmit}
          onChange={() => {
            setDirtyForm(true);
          }}
        />
      </Box>

      <FdButton
        variant="contained"
        color="primary"
        size="medium"
        onClick={handleCreateEvent}
        sx={{ marginTop: "20px", marginBottom: "10px" }}
        ButtonName="Create Event"
      />
      <FdButton sx={classes.issueButton} onClick={handleIssueButton} ButtonName="Go back" variant="text" />
    </Box>

    <SimpleActionDialog
      open={actionDialog.open}
      title={actionDialog.title}
      content={actionDialog.content}
      confirmLabel={actionDialog.confirmLabel}
      icon={actionDialog.icon}
      onConfirm={actionDialog.onConfirm}
      onCancel={actionDialog.onCancel}
    />
  </>;
};

export default NewEventDetails;
